import React from "react";
import { CertificateIssuer, agentIssuerDetails } from "./certificateIssuer";

/**
 * The Employer Certificate (Annexure - B) exactly as it is posted to
 * POST /employer-certificate/add — the `#pdfContent` the PDF is rendered from.
 *
 * Shared by the Employer Certificate screen's Download dialog and by the PAN
 * form, which generates this certificate on its own for a CAT_DSA / CAT_PCO
 * application the moment the PAN form is generated (renderToStaticMarkup,
 * nothing mounted).
 * One template, so the two can never print different certificates.
 *
 * `data` is the application as the employer-certificate APIs return it
 * (list/pagination or get-application-by-id): retailerData, distributorData,
 * logos, randomNumber and the retailer* fields. The applicant block comes in
 * separately — see employerCertificateApplicant.
 */
export type EmployerCertificateTemplateProps = {
  data: any;
  applicantName: string;
  fatherName: string;
  address: string;
  place: string;
  todayDate: string;
  /**
   * The office address as the PAN form holds it (DSA: the agent's counter,
   * PCO: the head office). Empty falls back to the agent's profile address.
   */
  officeAddress?: string;
  /** The applicant's cropped photo, printed in the "Affix photograph" box. */
  photoUrl?: string;
  /**
   * Who issues the certificate, and their stamp.
   * PCO: the firm the application is filed under (findPcoFirm) — its details
   * replace the agent's, and its combined seal & signature is stamped.
   * DSA: the agent (dsaIssuerFor) with their own seal and signature.
   * The stamp prints twice — across the photo and beside
   * "(Signature) / Office Seal".
   */
  issuer?: CertificateIssuer | null;
};

// The "Affix photograph" box stays at its printed height; the photo pasted
// on it is the PAN form photo's 4.5 cm and overlaps the text below the box.
const PHOTO_BOX_HEIGHT = "100px";
const PHOTO_HEIGHT = "calc(45mm + 10px)";
/** A wide (landscape) photo is shrunk to fit rather than run off the page. */
const PHOTO_MAX_WIDTH = "45mm";

// Seal & signature as stamped on the certificate. `multiply` drops a white
// background, so even a JPEG seal does not paint a white box over the photo.
const SEAL_WIDTH = "48mm";
const sealImageStyle: React.CSSProperties = {
  width: SEAL_WIDTH,
  maxHeight: "32mm",
  objectFit: "contain",
  mixBlendMode: "multiply",
  display: "block",
};

// DSA stamps the seal and signature as two images: the seal, with the
// signature written across its centre. Both reach here with their paper
// removed (cleanDsaStamp), so neither paints a white box over the other.
const DSA_SEAL_WIDTH = "38mm";
const dsaSealStyle: React.CSSProperties = {
  width: DSA_SEAL_WIDTH,
  maxHeight: "20mm",
  objectFit: "contain",
  mixBlendMode: "multiply",
  display: "block",
};
const dsaSignatureStyle: React.CSSProperties = {
  width: "30mm",
  maxHeight: "11mm",
  objectFit: "contain",
  mixBlendMode: "multiply",
  display: "block",
};

const hasStamp = (issuer?: CertificateIssuer | null) =>
  !!(issuer?.sealSignatureImage || issuer?.sealImage || issuer?.signatureImage);

/**
 * The issuer's stamp. PCO's combined image prints exactly as before (`place`
 * merged into its own style); DSA's seal and signature are stacked in a box
 * the same width as the PCO stamp, signature centred on the seal, and
 * `place` goes on that box. Either image may be missing — the other prints alone.
 *
 * `align="left"` starts the DSA seal at the box's left edge — where the PCO
 * image starts — so across the photo it overlaps the photo as much as the
 * PCO stamp does. Centred, it sat ~7mm further right, mostly off the photo.
 */
export const SealStamp = ({
  issuer,
  place,
  align = "center",
}: {
  issuer?: CertificateIssuer | null;
  place?: React.CSSProperties;
  align?: "left" | "center";
}) => {
  if (!issuer) return null;
  if (issuer.sealSignatureImage) {
    return (
      <img
        src={issuer.sealSignatureImage}
        alt="Seal & signature"
        style={{ ...sealImageStyle, ...place }}
      />
    );
  }
  if (!issuer.sealImage && !issuer.signatureImage) return null;
  return (
    <div
      style={{
        width: SEAL_WIDTH,
        display: "flex",
        alignItems: "center",
        justifyContent: align === "left" ? "flex-start" : "center",
        ...place,
      }}
    >
      {/* Sized to the seal, so the signature centres on the seal itself. */}
      <div style={{ position: "relative", display: "inline-block" }}>
        {issuer.sealImage && (
          <img
            src={issuer.sealImage}
            alt="Seal"
            style={{ ...dsaSealStyle, margin: 0 }}
          />
        )}
        {issuer.signatureImage && (
          <img
            src={issuer.signatureImage}
            alt="Signature"
            style={
              issuer.sealImage
                ? {
                    ...dsaSignatureStyle,
                    // Sized off the seal, so it stays inside the seal's
                    // middle band between its top and bottom lines.
                    width: "65%",
                    maxHeight: "48%",
                    margin: 0,
                    position: "absolute",
                    left: "50%",
                    top: "50%",
                    transform: "translate(-50%, -50%)",
                  }
                : { ...dsaSignatureStyle, margin: 0 }
            }
          />
        )}
      </div>
    </div>
  );
};

// The fill-in rule the footer's Place / Date values print on.
const dottedValueStyle: React.CSSProperties = {
  flex: 1,
  marginLeft: "12px",
  paddingBottom: "3px",
  borderBottom: "1px dotted #000",
  minHeight: "20px",
};

/**
 * "Details of the Individual issuing the certificate". The issuer's own
 * details when one is given (an empty box prints N/A); otherwise the agent
 * who filed the application, as before.
 */
const issuerDetails = (data: any, issuer?: CertificateIssuer | null) => {
  if (issuer) {
    return {
      fullName: issuer.fullName || "N/A",
      designation: issuer.designation || "Proprietor",
      panNumber: issuer.panNumber || "N/A",
      officeAddressWithLocation: issuer.officeAddressWithLocation || "N/A",
      telephone: issuer.telephone || "N/A",
      mobile: issuer.mobile || "N/A",
    };
  }
  return agentIssuerDetails(data);
};

const EmployerCertificateTemplate = ({
  data: businessEnquiryData,
  applicantName,
  fatherName,
  address,
  place,
  todayDate,
  officeAddress,
  photoUrl,
  issuer,
}: EmployerCertificateTemplateProps) => {
  // One address for both the letterhead line and "Office Address", so the two
  // always match.
  // The firm name is the retailer's / distributor's own profile firm name —
  // never the Employer Certificate Logo entry's.
  const orgAddress = officeAddress
    ? officeAddress
    : businessEnquiryData?.appliedByType === "RETAILER"
    ? `C/O ${businessEnquiryData?.retailerFirmName}, ${businessEnquiryData?.appliedByUserCurrentAddress || businessEnquiryData?.retailerFirmAddress || "N/A"}`
    : businessEnquiryData?.appliedByType === "DISTRIBUTOR"
      ? businessEnquiryData?.distributorData?.firmName && businessEnquiryData?.appliedByUserCurrentAddress
        ? `C/O ${businessEnquiryData?.distributorData?.firmName}, ${businessEnquiryData?.appliedByUserCurrentAddress}`
        : "N/A"
      : "N/A";

  // PCO: the firm's own letterhead (logo + address already printed on it) is
  // the page background, so no logo / address line is typed above.
  const letterhead = issuer?.letterheadImage;

  return (
      <div
      id="pdfContent"
      style={{
        width: "210mm",
        margin: "auto",
        padding: 40,
        marginBottom: "10px",
        fontFamily: "Arial, sans-serif",
        fontSize: "14px",
        lineHeight: "1.5",
        ...(letterhead
          ? {
              position: "relative",
              boxSizing: "border-box",
              minHeight: "297mm",
              paddingTop: "47mm",
              paddingBottom: "18mm",
            }
          : {}),
      }}
    >
      {letterhead && (
        <img
          src={letterhead}
          alt="Letterhead"
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "210mm",
            height: "297mm",
            zIndex: 0,
          }}
        />
      )}
      {/* The letterhead's header + footer take ~65mm of the A4 page, so the
          paragraphs sit closer together to keep the certificate on one page. */}
      {letterhead && (
        <style>{`#pdfContent p { margin-bottom: 0.45rem !important; margin-top: 0 !important; }`}</style>
      )}
      <div style={{ position: "relative", zIndex: 1 }}>
      {/* Header Section */}
      {!letterhead && (
      <>
      <div style={{ textAlign: "center", marginBottom: 10 }}>
        <img
          src={businessEnquiryData?.logos?.image || ""}
          alt="Company Logo"
          style={{ width: "100%", height: "120px" }}
        />
        <p style={{ margin: 'auto auto 1rem', marginTop: 10, fontWeight: 700, textTransform: "capitalize" }}>
          {orgAddress}

        </p>
      </div>
      <hr />
      </>
      )}
      {/* Title Section */}
      <div style={{ textAlign: "center", fontSize: 24, fontWeight: "bold", marginBottom: 10, textDecoration: "underline" }}>
        Annexure - B
      </div>
      <p style={{ textAlign: "center", marginBottom: 10 }}>
        (Certificate to be used by the Employer on the letterhead of the organization/institution under sub-rule (4) of rule
        114 of the Income-tax Rules, 1962)
      </p>
      {/* Right-side Box — kept at its printed size (100px). The applicant's
          photo is pasted from the box's top-left corner at full photo size, so
          it runs past the box and over the attestation note below it. The
          firm's seal & signature (PCO) is stamped across the photo's lower edge
          — half on the photo, half on the certificate. */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div style={{ width: "60%" }}></div>
        <div style={{ width: "40%" }}>
          <div style={{ position: "relative", width: "60%", margin: "15px 0px" }}>
            {/* With a photo the box keeps its height for the layout but loses
                its border: a portrait photo is narrower than the box, and the
                box's edge used to show beside it. The photo frame has its own. */}
            <div
              style={{
                borderStyle: "solid",
                borderWidth: 2,
                borderColor: photoUrl ? "transparent" : "black",
                boxSizing: "border-box",
                height: photoUrl ? PHOTO_BOX_HEIGHT : undefined,
              }}
            >
              {!photoUrl && (
                <p style={{ fontSize: 14, margin: 0, padding: 8 }}>
                  (Affix same photograph as affixed on PAN application form)
                </p>
              )}
            </div>
            {photoUrl && (
              // Bordered frame around the photo, so its edge shows on every
              // side where it overlaps the box and the text below.
              //
              // The frame takes the photo's own shape: fixed height, width from
              // the photo. It used to be a fixed, near-square box with
              // object-fit: cover, which cut the top and bottom off a portrait
              // passport photo — the applicant's head was printed cut off.
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  height: PHOTO_HEIGHT,
                  border: "2px solid #000",
                  boxSizing: "border-box",
                  background: "#fff",
                  overflow: "hidden",
                  zIndex: 2,
                }}
              >
                <img
                  src={photoUrl}
                  alt="Applicant"
                  style={{
                    height: "100%",
                    width: "auto",
                    maxWidth: PHOTO_MAX_WIDTH,
                    objectFit: "contain",
                    display: "block",
                  }}
                />
              </div>
            )}
            <SealStamp
              issuer={issuer}
              align="left"
              place={{
                position: "absolute",
                left: "55%",
                top: "calc(27mm + 10px)",
                zIndex: 3,
              }}
            />
          </div>
          <p style={{ fontSize: 14, marginBottom: 15 }}>
            (To be attested by issuing authority with his/her signature and
            rubber stamp appearing half on the photograph and half of the
            certificate)
          </p>
        </div>
      </div>
      {/* Certificate Content */}
      <p style={{margin:'auto auto 1rem'}}>
        <strong>It is hereby certified that Sh./Smt./Kum:</strong>{" "}
        <span style={{ textDecoration: "dotted" }}>{applicantName || "..........................."}</span>, Son/Daughter of:{" "}
        <span style={{ textDecoration: "dotted" }}>{fatherName || "..........................."}</span> is employed with us since <span style={{ textDecoration: "underline" }}>{businessEnquiryData?.randomNumber || 2}</span> Years.
        He/She is presently working at the following address of the organization:
      </p>
      <p style={{margin:'auto auto 1rem'}}>
        <strong>Office Address:</strong>{" "}
        <span style={{ textDecoration: "underline" }}>    
       
          {orgAddress}

</span>
      </p>
      <p style={{margin:'auto auto 1rem'}}>
        <strong>The residential address of the applicant as verified by us is given below:</strong>
      </p>
      <p style={{margin:'auto auto 1rem'}}>
        Residential Address: <span style={{ textDecoration: "dotted" }}>{address || "........................................................................."}</span>
      </p>
      <p style={{margin:'auto auto 1rem'}}>Registration Number of the Company/Institution etc.: N/A</p>
      <p style={{margin:'auto auto 1rem'}}>PAN of the Company/Institution: N/A</p>
      <p style={{margin:'auto auto 1rem'}}><strong>Details of the Individual issuing the certificate:</strong></p>
      <p style={{margin:'auto auto 1rem'}}>
        Full Name: <span style={{ textDecoration: "underline", textTransform: "capitalize", }}>
          {issuerDetails(businessEnquiryData, issuer).fullName}
        </span>
      </p>
      <p style={{margin:'auto auto 1rem'}}>Designation: {issuerDetails(businessEnquiryData, issuer).designation}</p>
      <p style={{margin:'auto auto 1rem'}}>
        PAN of the Individual: <span style={{ textDecoration: "underline", textTransform: "uppercase", }}>
          {issuerDetails(businessEnquiryData, issuer).panNumber}
        </span>
      </p>
      <p style={{margin:'auto auto 1rem'}}>
        Office Address with Location: <span style={{ textDecoration: "underline", textTransform: "capitalize", }}>
          {issuerDetails(businessEnquiryData, issuer).officeAddressWithLocation}
        </span>
      </p>

      <div style={{ display: "flex", justifyContent: "space-between", marginRight: hasStamp(issuer) ? 20 : 70, marginTop: 10 }}>
        <div style={{ width: "320px" }}>
          <p style={{margin:'auto auto 1rem'}}>Telephone: {issuerDetails(businessEnquiryData, issuer).telephone}</p>
          <p style={{margin:'auto auto 1rem'}}>
            Mobile: <span style={{ textDecoration: "underline" }}>
              {issuerDetails(businessEnquiryData, issuer).mobile}
            </span>
          </p>
          {/* Place sits above Date, each value on its own dotted rule — the
              shape the printed certificate is signed against. */}
          <div
            style={{
              display: "flex",
              alignItems: "flex-end",
              marginBottom: "1rem",
            }}
          >
            <span style={{ width: "55px", flexShrink: 0 }}>Place</span>
            <span style={{ flexShrink: 0 }}>:</span>
            <span style={{ ...dottedValueStyle, textTransform: "uppercase" }}>
              {place || "N/A"}
            </span>
          </div>

          <div style={{ display: "flex", alignItems: "flex-end" }}>
            <span style={{ width: "55px", flexShrink: 0 }}>Date</span>
            <span style={{ flexShrink: 0 }}>:</span>
            <span style={{ ...dottedValueStyle }}>{todayDate}</span>
          </div>
        </div>
        <div style={{ textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center" }}>
          <SealStamp issuer={issuer} />
          <p style={{ margin: hasStamp(issuer) ? "4px 0 0" : undefined }}>(Signature)</p>
          <p style={{ margin: hasStamp(issuer) ? 0 : undefined }}>Office Seal</p>
        </div>
      </div>
      </div>
    </div>
  );
};

export default EmployerCertificateTemplate;
